import type { ParseProgress } from '../types';

/** Steps shown on the "읽는 중" screen. Parse phases plus our own wrappers. */
export type ProgressStage = 'downloading' | 'reading' | 'unzipping' | 'parsing' | 'analyzing';

export const STAGE_TITLES: Readonly<Record<ProgressStage, string>> = {
  downloading: '메일로 받은 대화를 가져오고 있어요',
  reading: '대화를 읽고 있어요',
  unzipping: '대화를 읽고 있어요',
  parsing: '대화를 읽고 있어요',
  analyzing: '성적표를 만들고 있어요',
};

export const STAGE_LABELS: Readonly<Record<ProgressStage, string>> = {
  downloading: '메일 파일 받는 중',
  reading: '파일 여는 중',
  unzipping: '압축 푸는 중',
  parsing: '메시지 읽는 중',
  analyzing: '채점하는 중',
};

/** Announced once per stage through the polite live region. */
export const STAGE_ANNOUNCEMENTS: Readonly<Record<ProgressStage, string>> = {
  downloading: '메일로 받은 대화 파일을 가져오고 있어요.',
  reading: '대화 파일을 열고 있어요.',
  unzipping: '압축 파일을 풀고 있어요.',
  parsing: '메시지를 읽고 있어요.',
  analyzing: '성적표를 만들고 있어요.',
};

/** Playful rotating copy. Purely decorative (hidden from screen readers). */
export const STAGE_QUIPS: Readonly<Record<ProgressStage, readonly string[]>> = {
  downloading: ['메일함에서 대화 꺼내는 중…', '받은 파일을 이 기기로 옮기는 중…'],
  reading: ['메시지 정주행 중…', '대화 파일 펼치는 중…', '스크롤 끝까지 내리는 중…'],
  unzipping: ['압축 푸는 중… 조심조심', '대화 파일만 쏙 꺼내는 중…'],
  parsing: [
    '메시지 정주행 중…',
    '선톡 횟수 세는 중…',
    'ㅋ 개수 세는 중… 정중하게',
    '새벽 2시 메시지 모으는 중…',
    '답장 속도 재는 중…',
    '말버릇 받아 적는 중…',
  ],
  analyzing: ['성적 매기는 중…', '담임 선생님 한마디 고르는 중…', '상장에 이름 쓰는 중…', '달력에 도장 찍는 중…'],
};

export function stageQuip(stage: ProgressStage, tick: number): string {
  const quips = STAGE_QUIPS[stage];
  return quips[((Math.trunc(tick) % quips.length) + quips.length) % quips.length]!;
}

export function stageForParse(progress: ParseProgress): ProgressStage {
  return progress.phase;
}

function ratio(progress: ParseProgress): number {
  if (!(progress.totalBytes > 0)) return 0;
  return Math.min(1, Math.max(0, progress.loadedBytes / progress.totalBytes));
}

export interface ParseProgressTracker {
  /** Returns a monotonic 0-100 percentage for the bar. */
  update(progress: ParseProgress): number;
  /** Parsing finished. */
  finish(): number;
  readonly percent: number;
}

/**
 * Maps parser progress onto one bar. The parser streams: reading/unzipping
 * bytes is most of the work and fills the first 90%; the final 'parsing' step
 * fills the rest (or the whole bar when no input stage was reported).
 * The value never moves backwards, and stays below 100 until finish().
 */
export function createParseProgressTracker(): ParseProgressTracker {
  let percent = 0;
  let sawInputStage = false;
  const INPUT_END = 90;
  const PARSE_END = 96;
  return {
    update(progress) {
      const value = ratio(progress);
      let next: number;
      if (progress.phase === 'parsing') {
        next = sawInputStage ? INPUT_END + (PARSE_END - INPUT_END) * value : PARSE_END * value;
      } else {
        sawInputStage = true;
        next = INPUT_END * value;
      }
      percent = Math.max(percent, Math.min(PARSE_END, Math.floor(next)));
      return percent;
    },
    finish() {
      percent = 100;
      return percent;
    },
    get percent() {
      return percent;
    },
  };
}
